import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #ffffff;
  margin-bottom: 2rem;
  text-align: center;
`;

const Content = styled.div`
  color: #cccccc;
  line-height: 1.6;
  font-size: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #ffffff;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
`;

const Small = styled.small`
  color: #aaaaaa;
`;

const List = styled.ul`
  padding-left: 1.25rem;
`;

const TOS: React.FC = () => {
  return (
    <Container>
      <Title>Terms of Service</Title>
      <Content>
        <p>
          Last updated: <Small>August 21, 2025</Small>
        </p>

        <p>
          These Terms of Service ("Terms") govern your access to and use of <strong>YouMeditation</strong> ("the Service"), owned and operated by <strong>TAF Technology Inc.</strong> ("Company", "we", "us", or "our"), including the website, applications, content, features, and any related services. By accessing or using the Service, you agree to be bound by these Terms.
        </p>

        
        <SectionTitle>Eligibility and account registration</SectionTitle>
        <p>
          You must be at least <strong>13</strong> years old to use the Service. If you create an account, you agree to provide accurate, current, and complete information and to keep it updated. You are responsible for safeguarding your credentials and for all activities on your account. Notify us immediately of any unauthorized use or security breach.
        </p>

        <SectionTitle>Subscription, payments, and renewals</SectionTitle>
        <p>
          Certain features may require a paid subscription or purchase. By starting a paid plan, you authorize us (and our payment processor) to charge you the applicable fees, taxes, and any renewal amounts unless you cancel before the renewal date.
        </p>
        <List>
          <li>Pricing and plans: see <a href="/subscription">Subscription</a></li>
          <li>Billing interval: monthly or annual billing available</li>
          <li>Free trial: 3-day free trial on annual subscriptions</li>
          <li>Refund policy: no refunds (except as required by law)</li>
          <li>Promotions/discounts: apply only to the first billing period of a new subscription</li>
        </List>

        <SectionTitle>Use of the Service</SectionTitle>
        <p>
          Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your personal, non-commercial use (unless otherwise agreed in writing).
        </p>
        <List>
          <li>Acceptable use: Do not misuse the Service, interfere with its operation, or access it using a method other than the interfaces we provide.</li>
          <li>Prohibited conduct: No scraping, reverse engineering, automated data collection, or use that infringes rights, violates laws, or harms others.</li>
          <li>Health and safety: The Service may include wellness or meditation features. It is not a medical device, diagnosis, or treatment. Consult a medical professional for any health concerns.</li>
        </List>

        <SectionTitle>User content and conduct</SectionTitle>
        <p>
          You may submit, upload, or post content, including text, audio, images, comments, or other materials ("User Content"). You retain ownership of your User Content. You grant us a worldwide, non-exclusive, royalty-free license to host, store, reproduce, modify, adapt, publish, translate, distribute, publicly perform, and display your User Content as needed to operate, promote, and improve the Service.
        </p>
        <List>
          <li>You represent and warrant that you have all rights necessary to submit the User Content and that it does not infringe, violate, or misappropriate any rights or laws.</li>
          <li>We may remove or disable access to any User Content that violates these Terms or applicable law.</li>
          <li>Community standards: We prohibit content that is illegal; incites or depicts violence; is hateful, harassing, or discriminatory; is sexual or exploitative; or is otherwise inappropriate. We may remove such content and may suspend or terminate accounts and/or ban users at our discretion.</li>
        </List>

        <SectionTitle>Intellectual property</SectionTitle>
        <p>
          The Service and all content and materials provided by us (excluding User Content) are owned by or licensed to the Company and are protected by intellectual property and other laws. You may not copy, modify, distribute, sell, or lease any part of our Service or included content, nor may you reverse engineer or attempt to extract the source code, unless laws prohibit those restrictions or you have our written permission.
        </p>

        <SectionTitle>AI-generated content and audio mixing features</SectionTitle>
        <p>
          All content created on the Service (including AI-generated content and mixed audio) is owned by the Company and is protected by intellectual property and other laws. You may not copy, modify, distribute, sell, or lease any part of our Service or included content, nor may you reverse engineer or attempt to extract source code, source material, or audio files, unless laws prohibit those restrictions or you have our written permission.
        </p>


        <SectionTitle>Privacy</SectionTitle>
        <p>
          Your use of the Service is subject to our Privacy Policy, which explains how we collect, use, and share information. See our <a href="/privacy">Privacy Policy</a>.
        </p>

        <SectionTitle>Third-party services</SectionTitle>
        <p>
          The Service may integrate with third-party services (e.g., payment processors, content providers). Your use of those services may be subject to the third party's terms and privacy policies. We are not responsible for third-party services.
        </p>

        <SectionTitle>Termination</SectionTitle>
        <p>
          We may suspend or terminate your access to the Service (including any paid plan, subject to applicable law and refund terms) if you materially or repeatedly breach these Terms, we are investigating suspected misconduct, or if required by law. You may stop using the Service at any time.
        </p>

        <SectionTitle>Disclaimers</SectionTitle>
        <p>
          The Service is provided on an "AS IS" and "AS AVAILABLE" basis. To the maximum extent permitted by law, we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, title, and non-infringement. We do not warrant that the Service will be uninterrupted, secure, or error-free, or that content will be accurate or reliable.
        </p>

        <SectionTitle>Limitation of liability</SectionTitle>
        <p>
          To the maximum extent permitted by law, the Company and its affiliates, officers, employees, agents, partners, and licensors will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from your use of or inability to use the Service. Our total liability for any claims relating to the Service shall not exceed the greater of (a) the amount you paid to us in the 12 months preceding the event giving rise to liability, or (b) USD <strong>____</strong>.
        </p>

        <SectionTitle>Indemnification</SectionTitle>
        <p>
          You agree to indemnify, defend, and hold harmless the Company and its affiliates, officers, employees, agents, partners, and licensors from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or in any way connected with your access to or use of the Service, your User Content, or your breach of these Terms.
        </p>

        <SectionTitle>Governing law; venue</SectionTitle>
        <p>
          Governing law: <strong>North Carolina, USA</strong>
          <br />
          Jurisdiction for disputes: <strong>Davidson County, NC, USA</strong>
          <br />
          Consumer mandatory rights in your jurisdiction are not affected.
        </p>

        <SectionTitle>Dispute resolution</SectionTitle>
        
        <List>
          <li>Binding Arbitration: Any dispute arising from these Terms of Service will be resolved by final and binding arbitration in Davidson County, North Carolina.</li>
          <li>Administrator of Arbitration: The arbitration will be administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules.</li>
          <li>Number of Arbitrators: The arbitration will use a single neutral arbitrator</li>
          <li>Location of Arbitration: The legal place of arbitration will be Davidson County, North Carolina.</li>
          <li>Language of Arbitration: The arbitration will be conducted in English.</li>
          <li>Opt-Out Period: You must notify us in writing within 30 days of the first notice of arbitration to opt out of arbitration.</li>
          <li>Class-Action Waiver: You agree that you may only bring claims on your own behalf and not on behalf of any other person or entity.</li>
          <li>Confidentiality: Arbitration proceedings and decisions will be confidential, unless required by law</li>
          <li>Discovery: Discovery will be completed within a reasonable timeframe set by the arbitrator</li>
        </List>

        <SectionTitle>Digital Millennium Copyright Act (DMCA)</SectionTitle>
        <p>
          If you believe content on the Service infringes your copyright, please send a notice to our designated agent with the following: (1) electronic signature; (2) identification of the copyrighted work; (3) identification of the infringing material; (4) your contact information; (5) a statement of good-faith belief; and (6) a statement under penalty of perjury regarding the accuracy of the notice. Designated Agent details:
        </p>
        <List>
          <li>Name/Department: <strong>YouMeditation Legal Department</strong></li>
          <li>Email: <strong>dmca@youmeditation.com</strong></li>
        </List>

        <SectionTitle>App stores and platform terms</SectionTitle>
        <p>
          If you access the Service via an app store (e.g., Apple App Store, Google Play), you acknowledge the applicable store's terms and that the store is not responsible for support or claims. Platform-specific terms may apply. We are not currently distributed via app stores.
        </p>

        <SectionTitle>Changes to the Service and Terms</SectionTitle>
        <p>
          We may modify the Service and these Terms from time to time. If changes materially affect your rights, we will provide notice by email or in-app notice. The updated Terms will be effective on the date indicated, and your continued use of the Service after that date constitutes acceptance.
        </p>

        <SectionTitle>Communications</SectionTitle>
        <p>
          By creating an account, you may receive transactional communications. You can opt into or out of marketing communications as provided in our Privacy Policy and applicable laws. Message and data rates may apply.
        </p>

        <SectionTitle>International users</SectionTitle>
        <p>
          We operate the Service from North Carolina, USA. You are responsible for compliance with local laws. If you are a resident of the EU/EEA/UK, additional consumer and data rights may apply.
        </p>

        <SectionTitle>Minors</SectionTitle>
        <p>
          The Service is not directed to children. If you are under the age specified in the Eligibility section, you may not use the Service. If we learn that a child has provided personal data without parental consent where required, we will take steps to delete such information.
        </p>

        <SectionTitle>Force majeure</SectionTitle>
        <p>
          We will not be liable for any delay or failure to perform due to causes beyond our reasonable control, including acts of God, natural disasters, war, terrorism, labor conditions, governmental actions, internet or telecommunication failures, or utility outages.
        </p>

        <SectionTitle>Entire agreement; severability; waiver</SectionTitle>
        <p>
          These Terms constitute the entire agreement between you and the Company regarding the Service and supersede prior agreements. If any provision is held invalid, the remaining provisions will remain in full force and effect. Our failure to enforce any right or provision is not a waiver of such right or provision.
        </p>

        <SectionTitle>Contact</SectionTitle>
        <p>
          For questions about these Terms, contact us at: <strong>support@youmeditation.com</strong>
        </p>


      </Content>
    </Container>
  );
};

export default TOS;